import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  axios,
  AxiosError,
  CreditCard,
  CreditCardService,
  ErrorResponse,
  ParentPlanService,
  ParentPlan,
  ParentService,
  Plan,
  PlanService,
  TermsAcceptanceService,
  SignupReasonId,
} from 'us-web-services';
import { Alert } from '../Alert/Alert';
import { Modal } from '../Modal';
import { FamilyPaywallMembership } from './FamilyPaywallMembership';
import { FamilyPaywallPayment } from './FamilyPaywallPayment';
import { PaywallProps } from './PaywallProps';

interface PlanConfigItem {
  id: number;
  order: number;
  label: string;
  isDefault: boolean;
}

type PlanConfig = {
  [SignupReasonId.ONE_TIME]: PlanConfigItem[];
  default: PlanConfigItem[];
};

const PLAN_CONFIGS: PlanConfig = {
  [SignupReasonId.ONE_TIME]: [
    { id: 19, order: 1, label: '1-Month Access Pass', isDefault: false },
    { id: 5, order: 2, label: 'Annual Membership', isDefault: true },
    { id: 7, order: 3, label: 'Monthly Membership', isDefault: false },
  ],
  default: [
    { id: 22, order: 1, label: 'Monthly Plan', isDefault: false },
    { id: 23, order: 2, label: 'Quarterly Plan', isDefault: true },
    { id: 26, order: 3, label: 'Annual Plan', isDefault: false },
  ]
};

export const FamilyPaywallModal = (props: PaywallProps): JSX.Element => {
  const { t } = useTranslation();
  const { header, onClose, onSuccess, planId, sitter, showModal, paymentOnly = false, userId } = props;

  const [alertMessage, setAlertMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(showModal);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [creditCard, setCreditCard] = useState<CreditCard>();
  const [showPaymentScreen, setShowPaymentScreen] = useState<boolean>(paymentOnly);
  const [selectedPlanId, setSelectedPlanId] = useState<number | undefined>(planId);

  let headerText;
  if (header) {
    headerText = header;
  } else if (sitter) {
    headerText = t('components.modals.parentPaywall.headers.contactSitter', { name: sitter.firstName });
  } else {
    headerText = t('components.modals.parentPaywall.headers.selectPlan');
  }

  const getSelectedPlan = (): Plan | undefined => plans.find(plan => plan.id === selectedPlanId);

  const getUserSignupReason = async (): Promise<SignupReasonId|null> => {
    try {
      const signupReason = (await ParentService.get(userId)).data.data.signupReason?.reason?.id || null;

      return signupReason;
    } catch (error) {
      console.error('Failed to load user signup reason:', error);

      return null;
    }
  };

  const loadUserPlans = async (reason: SignupReasonId | null): Promise<void> => {
    try {
      const paymentPlans = (await PlanService.getByFilter({
        params: {
          fields: 'discount',
          parentId: userId,
          userId,
        },
      })).data.data;

      const planType = reason === SignupReasonId.ONE_TIME ? SignupReasonId.ONE_TIME : 'default';
      const config = PLAN_CONFIGS[planType];

      // Filter and sort plans according to config
      const planMap = new Map(paymentPlans.map(plan => [plan.id, plan]));

      const sortedPlans = config
        .filter(configItem => planMap.has(configItem.id))
        .sort((a, b) => a.order - b.order)
        .map(configItem => planMap.get(configItem.id)!);

      setPlans(sortedPlans);

      if (!selectedPlanId && sortedPlans.length > 0) {
        const defaultPlan = config.find(plan => plan.isDefault);
        if (defaultPlan && planMap.has(defaultPlan.id)) {
          setSelectedPlanId(defaultPlan.id);
        } else if (sortedPlans.length > 0) {
          setSelectedPlanId(sortedPlans[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const loadPlans = async (): Promise<void> => {
    const reason = await getUserSignupReason();
    await loadUserPlans(reason);
  };

  const getCreditCard = async (): Promise<void> => {
    const response = (await CreditCardService.findByFilter({
      params: {
        active: true,
        userId,
      },
    })).data;

    setCreditCard(response.data[0]);
  };

  const setErrorMessage = (message: string): void => {
    setIsError(true);
    setAlertMessage(message);
  }

  const setSuccessMessage = (message: string): void => {
    setIsError(false);
    setAlertMessage(message);
  }

  const handleClose = (): void => {
    setModalIsOpen(false);

    if (!paymentOnly) {
      setShowPaymentScreen(false);
    }

    setErrorMessage('');

    onClose();
  };

  const storeTermsAcceptance = async (plan: ParentPlan): Promise<void> => {
    const termsData = {
      accepted: Date.now(),
      planId: plan.planId,
      terms: {
        alias: 'tos',
      },
      userId,
      userPlanId: plan.id,
    };

    try {
      await TermsAcceptanceService.create(termsData);
    } catch (e) {
      console.warn(e);
    }
  };

  const handleError = (e: unknown): void => {
    console.warn(e);

    if (axios.isAxiosError(e)) {
      const error = e as AxiosError<ErrorResponse, unknown>;

      if (error.response?.data.errors && error.response.data?.errors?.length) {
        setErrorMessage(error.response.data?.errors[0].message);
      } else {
        setErrorMessage(t('components.modals.parentPaywall.genericError'));
      }
    } else {
      setErrorMessage(t('components.modals.parentPaywall.genericError'));
    }
  };

  const createPlan = async (): Promise<void> => {
    const selectedPlan = getSelectedPlan();
    if (!selectedPlan) {
      return;
    }

    try {
      const plan = {
        planId: selectedPlan.id,
        userId,
      };
      const parentPlan = (await ParentPlanService.create(plan)).data.data;

      await storeTermsAcceptance(parentPlan);
      onSuccess(selectedPlan);
    } catch (e) {
      handleError(e);
    }
  };

  useEffect(() => {
    loadPlans();
    getCreditCard();
  }, []);

  useEffect(() => {
    setModalIsOpen(showModal);
  }, [showModal]);

  useEffect(() => {
    setSelectedPlanId(planId);
  }, [planId]);

  useEffect(() => {
    if (paymentOnly) {
      setShowPaymentScreen(paymentOnly);
    }
  }, [paymentOnly]);

  const handleNextStep = (): void => {
    setShowPaymentScreen(true);
  }

  return (
    <Modal isOpen={modalIsOpen} closeWithOverlay={false} onClose={handleClose} removePadding className='max-w-[820px] text-base'>
      <div className='flex items-start justify-between text-xl font-bold bg-sapphire text-white p-4'>
        <div className='flex items-center'>
          {sitter?.pictureUrl &&
          <img
            alt={`${sitter?.firstName}`}
            className='w-10 inline-block rounded-full mr-4'
            src={sitter?.pictureUrl}
          />}
          {headerText}
        </div>
        <button type='button' aria-label='Close modal' className='leading-none text-3xl' onClick={handleClose}>
          <span aria-hidden='true'>&times;</span>
        </button>
      </div>
      <Alert
        color={isError ? 'error': 'success'}
        isOpen={!!alertMessage}
      >
        {alertMessage}
      </Alert>
      <div className={`p-4 lg:px-5 lg:pb-5${!paymentOnly ? ' grid grid-cols-1 md:grid-cols-3 gap-6' : ''}`}>
        <div className={`${!paymentOnly ? 'col-span-1 md:col-span-2' : ''}`}>
          {showPaymentScreen ?
            <FamilyPaywallPayment
              createPlan={createPlan}
              creditCard={creditCard}
              handleError={handleError}
              loadPlans={loadPlans}
              paywallProps={props}
              selectedPlan={getSelectedPlan()}
              setCreditCard={setCreditCard}
              setErrorMessage={setErrorMessage}
              setShowPaymentScreen={setShowPaymentScreen}
              setSuccessMessage={setSuccessMessage}
            /> :
            <FamilyPaywallMembership
              creditCard={creditCard}
              handleNextStep={handleNextStep}
              plans={plans}
              selectedPlanId={selectedPlanId}
              setSelectedPlanId={setSelectedPlanId}
            />}
        </div>
        {!paymentOnly && (
        <div className='col-span-1'>
          <p className='text-lg font-bold'>{t('components.modals.parentPaywall.renewalOffer.title')}</p>
          <p className='text-sm leading-snug'>{t('components.modals.parentPaywall.renewalOffer.body')}</p>
        </div> )}
      </div>
    </Modal>
  );
};
